import {
  createSlice,
  type SerializedError,
  type PayloadAction,
  type WithSlice,
} from '@reduxjs/toolkit';
import { type FetchBaseQueryError } from '@reduxjs/toolkit/query';

import { rootReducer } from '@verantos-dev/app-state-base';
import { type InputFieldConfig, type CohortBuilderModelLegacy } from '@verantos-dev/types-cohorts';

export interface CohortDefinitionState {
  /** Whether the cohort profile is loading */
  isLoading: boolean;

  /** Whether the cohort version (the dsl) is loading */
  isVersionLoading: boolean;

  /** Error state for the cohort definition */
  error: FetchBaseQueryError | SerializedError | undefined;

  /** Error state for the cohort version */
  versionError: FetchBaseQueryError | SerializedError | undefined;

  /** The staged cohort definition after translation from the backend or the research assistant */
  cohortDefinitionLegacy: CohortBuilderModelLegacy | null;

  /** Whether the cohort definition is editable by the user */
  isEditable: boolean;

  /** Whether the data has been staged for review by the research assistant */
  isStagedFromResearchAssistant: boolean;

  /** Whether the data has been staged for review by the user */
  isStagedFromUserEdit: boolean;

  /** Input field configuration map for the staged cohort definition */
  inputFieldConfigMap: Record<string, InputFieldConfig> | undefined;

  /** Last sync time for the staged cohort definition */
  lastSyncTime: string | null;
}

export enum CohortDefinitionStagingSource {
  RESEARCH_ASSISTANT = 'RESEARCH_ASSISTANT',
  BACKEND = 'BACKEND',
  USER_EDIT = 'USER_EDIT',
}

const initialState: CohortDefinitionState = {
  isLoading: true,
  isVersionLoading: true,
  error: undefined,
  versionError: undefined,
  cohortDefinitionLegacy: null,
  isEditable: false,
  isStagedFromResearchAssistant: false,
  isStagedFromUserEdit: false,
  lastSyncTime: null,
  inputFieldConfigMap: undefined,
};

const cohortDefinitionSlice = createSlice({
  name: 'cohortDefinitionStaging',
  initialState,
  reducers: {
    setCohortDefinitionTransitionStates: (
      state,
      action: PayloadAction<{
        isLoading: boolean;
        error: SerializedError | FetchBaseQueryError | undefined;
      }>
    ) => {
      state.isLoading = action.payload.isLoading;
      state.error = action.payload.error;
    },
    setCohortDefinitionVersionTransitionStates: (
      state,
      action: PayloadAction<{
        isLoading: boolean;
        error: SerializedError | FetchBaseQueryError | undefined;
      }>
    ) => {
      state.isVersionLoading = action.payload.isLoading;
      state.versionError = action.payload.error;
    },
    setCohortDefinitionLegacy: (
      state,
      action: PayloadAction<{
        cohortDefinitionLegacy: CohortBuilderModelLegacy;
        isStagedFromResearchAssistant: boolean;
        isEditable?: boolean;
      }>
    ) => {
      state.cohortDefinitionLegacy = action.payload.cohortDefinitionLegacy;
      state.isEditable = action.payload.isEditable ?? state.isEditable;
      state.isStagedFromResearchAssistant = action.payload.isStagedFromResearchAssistant;
      state.lastSyncTime = new Date().toISOString();
    },
    resetCohortDefinitionLegacy: (state) => {
      return {
        ...initialState,
      };
    },
  },
});

export const {
  setCohortDefinitionTransitionStates,
  setCohortDefinitionVersionTransitionStates,
  setCohortDefinitionLegacy,
  resetCohortDefinitionLegacy,
} = cohortDefinitionSlice.actions;

declare module '@verantos-dev/app-state-base' {
  // WithSlice utility assumes reducer is under slice.reducerPath
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type, @typescript-eslint/no-empty-interface
  export interface LazyLoadedSlices extends WithSlice<typeof cohortDefinitionSlice> {}
}

// Inject the slice into the root reducer
cohortDefinitionSlice.injectInto(rootReducer);
